import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/useAuth.js";
import UserLimits from "../components/dashboard/UserLimits.jsx";
import Statuses from "../components/dashboard/Statuses.jsx";
import Accounts from "../components/dashboard/Accounts.jsx";
import AccountSwitcher from "../components/dashboard/AccountSwitcher.jsx";
import WatchList from "../components/dashboard/WatchList.jsx";
import PurchasedLots from "../components/dashboard/PurchasedLots.jsx";
import Invoices from "../components/dashboard/Invoices.jsx";
import Payments from "../components/dashboard/Payments.jsx";
import BidTools from "../components/dashboard/BidTools.jsx";
import AdminTools from "../components/dashboard/AdminTools.jsx";
import Calculator from "../components/dashboard/Calculator.jsx";
import FeeBrowser from "../components/dashboard/FeeBrowser.jsx";
import SimilarCars from "../components/dashboard/SimilarCars.jsx";
import Carfax from "../components/dashboard/Carfax.jsx";
import PriceList from "../components/dashboard/PriceList.jsx";
import UserEvents from "../components/dashboard/UserEvents.jsx";
import CurrencyAndContacts from "../components/dashboard/CurrencyAndContacts.jsx";
import LocationInfo from "../components/dashboard/LocationInfo.jsx";
import LanguagePack from "../components/dashboard/LanguagePack.jsx";
import ReferenceLists from "../components/dashboard/ReferenceLists.jsx";

export default function PluginPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (!isAuthenticated) {
    return (
      <div className="container stack-lg">
        <div className="page-hero">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1>Plugin dashboard</h1>
            <span className="badge info">v0.15.2 · web</span>
          </div>
          <p>
            All sections of this dashboard (limits, accounts, watch list,
            purchased lots, invoices, payments, bid tools, etc.) require an
            AHistory user session.
          </p>
        </div>

        <div
          className="card"
          style={{ textAlign: "center", padding: "32px 20px" }}
        >
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            Authorization required
          </div>
          <div className="muted" style={{ marginBottom: 18 }}>
            Sign in with your AHistory account to use plugin features.
          </div>
          <button
            className="btn"
            onClick={() => navigate("/login", { state: { from: "/plugin" } })}
          >
            Log in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container stack-lg">
      <div className="page-hero">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h1>Plugin dashboard</h1>
          <span className="badge info">v0.15.2 · web</span>
        </div>
        <p>
          Every AHistory plugin handler exposed as a web section. Signed in as{" "}
          <b>{user?.name || "—"}</b>
          {user?.companyId && <> · company <b>{user.companyId}</b></>}
          {user?.id && <> · user <b>{user.id}</b></>}
          {user?.package && <> · plan <b>{user.package}</b></>}
          {user?.auctionFee && <> · fee level <b>{user.auctionFee}</b></>}.
        </p>
      </div>

      <Section title="Account">
        <UserLimits />
        <Statuses />
        <Accounts />
        <AccountSwitcher />
      </Section>

      <Section title="Car operations">
        <WatchList />
        <PurchasedLots />
        <Invoices />
        <Payments />
        <BidTools />
      </Section>

      <Section title="Tools">
        <Calculator />
        <FeeBrowser />
        <SimilarCars />
        <Carfax />
        <PriceList />
      </Section>

      <Section title="Administration">
        <AdminTools />
      </Section>

      <Section title="Activity & context">
        <UserEvents />
        <CurrencyAndContacts />
        <LocationInfo />
      </Section>

      <Section title="Language & translations">
        <LanguagePack />
      </Section>

      <Section title="Reference catalogs">
        <ReferenceLists />
      </Section>

      <details className="card">
        <summary className="muted small" style={{ cursor: "pointer" }}>
          Debug · normalized user payload
        </summary>
        <pre
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: 12,
            marginTop: 10,
            maxHeight: 400,
            overflow: "auto",
            background: "var(--color-bg-card-elevated)",
            padding: 12,
            borderRadius: 8,
          }}
        >
          {JSON.stringify(user, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="section-title">{title}</h2>
      <div className="grid grid-2" style={{ marginTop: 14 }}>
        {children}
      </div>
    </section>
  );
}
